import { defineStore } from 'pinia';
import { ElMessage } from 'element-plus';

import { getErrorMessage } from '@/api';
import { client } from '@/api/client';
import { UpdateTenantRequestDto } from '@/api/models/update-tenant-request.dto';
import { getStorageData, setStorageData } from '@/lib/local-storage';
import { TENANT_ID_STORAGE_KEY } from '@/constants/storage-keys';

type TenantState = {
  name: string;
  domain: string;
  casinoId: string;
  loading: boolean;
  updating: boolean;
  error: string | null;
};

export const useTenantStore = defineStore('tenant', {
  state: (): TenantState => ({
    name: '',
    domain: '',
    casinoId: getStorageData<string>(TENANT_ID_STORAGE_KEY) ?? '',
    loading: false,
    updating: false,
    error: null,
  }),
  actions: {
    async fetchCurrentTenant() {
      this.loading = true;
      this.error = null;

      try {
        const tenant = await client.tenantApi.getTenant();
        const { name, domain, casinoId } = tenant;
        this.name = name;
        this.domain = domain;
        this.casinoId = casinoId;
        setStorageData(TENANT_ID_STORAGE_KEY, casinoId || undefined);
        return tenant;
      } catch (err) {
        const message = getErrorMessage(err, 'Failed to load tenant');
        this.error = message;
      } finally {
        this.loading = false;
      }
    },

    async updateTenant(changes: UpdateTenantRequestDto) {
      this.updating = true;
      this.error = null;

      try {
        const tenant = await client.tenantApi.updateTenant(changes);
        const { name, domain, casinoId } = tenant;
        this.name = name;
        this.domain = domain;
        this.casinoId = casinoId;
        setStorageData(TENANT_ID_STORAGE_KEY, casinoId || undefined);
        ElMessage.success('Tenant updated successfully');
        return tenant;
      } catch (err) {
        const message = getErrorMessage(err, 'Error updating collection');
        ElMessage.error(message);
        this.error = message;
      } finally {
        this.updating = false;
      }
    },

    resetTenant() {
      this.$reset();
      setStorageData(TENANT_ID_STORAGE_KEY);
    },
  },
});
